import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TranslationSpendingSheet } from './SpendingParts';

const API = process.env.REACT_APP_BACKEND_URL;

// your reading status for each novel — set on the novel's own page
const STATUS_TABS = [
  { value: 'reading', label: 'reading' },
  { value: 'want', label: 'want to read' },
  { value: 'on_hold', label: 'on hold' },
  { value: 'finished', label: 'finished' },
  { value: 'dropped', label: 'dropped' },
];
const STATUS_LABELS = Object.fromEntries(STATUS_TABS.map(t => [t.value, t.label]));

// "2026-09" -> "Sep 2026"
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-').map(Number);
  if (!y || !mo) return m;
  return new Date(y, mo - 1, 1).toLocaleDateString([], { month: 'short', year: 'numeric' });
};

// the line under a novel's title
const readingLine = (n) => {
  if (n.reading_status === 'finished' && n.finished_month) return `finished ${formatMonth(n.finished_month)}`;
  if (n.reading_status === 'dropped' && n.finished_month) return `dropped ${formatMonth(n.finished_month)}`;
  if ((n.reading_status === 'reading' || n.reading_status === 'on_hold') && n.started_month) {
    return `since ${formatMonth(n.started_month)}`;
  }
  return '';
};

const PlusIcon = ({ size = 16, color = '#0e0b0f' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const GearIcon = ({ size = 15, color = '#c9935a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export default function Novels() {
  const [novels, setNovels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // which reading status the list is showing; remembered on this device
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem('novelsFilter') || 'all'; } catch (e) { return 'all'; }
  });
  const pickFilter = (value) => {
    setFilter(value);
    try { localStorage.setItem('novelsFilter', value); } catch (e) { /* not important */ }
  };

  // translation prompt settings
  const [showSettings, setShowSettings] = useState(false);
  const [promptDraft, setPromptDraft] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [isCustomPrompt, setIsCustomPrompt] = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);

  // recently deleted: books and chapters you can bring back for 30 days
  const [deletedCount, setDeletedCount] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);
  // translation spending: overall, this month, this week, today
  const [showSpending, setShowSpending] = useState(false);
  const [deleted, setDeleted] = useState(null);
  const [confirmForever, setConfirmForever] = useState('');
  const [busyDeleted, setBusyDeleted] = useState('');

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchNovels();
  }, []);

  const fetchNovels = () => {
    setLoading(true);
    axios.get(`${API}/novels?token=${token}`)
      .then(res => { setNovels(res.data.novels); setDeletedCount(res.data.recently_deleted || 0); })
      .catch(err => {
        setErrorMsg(`couldn't load novels: ${describeError(err)}`);
      })
      .finally(() => setLoading(false));
  };

  const describeError = (err) => {
    if (err.response) {
      const d = err.response.data?.detail;
      const detailText = Array.isArray(d)
        ? d.map(x => `${(x.loc || []).join('.')}: ${x.msg}`).join(' | ')
        : (typeof d === 'string' ? d : JSON.stringify(d));
      return `${err.response.status} — ${detailText || 'no detail'}`;
    }
    return err.message || 'network error';
  };

  const openSettings = () => {
    setShowSettings(true);
    setPromptSaved(false);
    setLoadingPrompt(true);
    axios.get(`${API}/settings/translation-prompt?token=${token}`)
      .then(res => {
        setPromptDraft(res.data.prompt || '');
        setDefaultPrompt(res.data.default_prompt || '');
        setIsCustomPrompt(!!res.data.is_custom);
      })
      .catch(err => setErrorMsg(`couldn't load prompt: ${describeError(err)}`))
      .finally(() => setLoadingPrompt(false));
  };

  const savePrompt = () => {
    setSavingPrompt(true);
    setPromptSaved(false);
    axios.put(`${API}/settings/translation-prompt`, { token, prompt: promptDraft })
      .then(res => {
        setIsCustomPrompt(!!res.data.is_custom);
        setPromptSaved(true);
      })
      .catch(err => setErrorMsg(`couldn't save prompt: ${describeError(err)}`))
      .finally(() => setSavingPrompt(false));
  };

  const resetPrompt = () => {
    // saving an empty prompt tells the backend to fall back to its built-in default
    setSavingPrompt(true);
    setPromptSaved(false);
    axios.put(`${API}/settings/translation-prompt`, { token, prompt: '' })
      .then(() => {
        setPromptDraft(defaultPrompt);
        setIsCustomPrompt(false);
        setPromptSaved(true);
      })
      .catch(err => setErrorMsg(`couldn't reset prompt: ${describeError(err)}`))
      .finally(() => setSavingPrompt(false));
  };

  const openDeleted = () => {
    setShowDeleted(true);
    setConfirmForever('');
    axios.get(`${API}/novels-deleted?token=${token}`)
      .then(res => setDeleted(res.data))
      .catch(err => setErrorMsg(`couldn't load recently deleted: ${describeError(err)}`));
  };
  const refreshDeleted = () => {
    fetchNovels();
    axios.get(`${API}/novels-deleted?token=${token}`).then(res => setDeleted(res.data)).catch(() => {});
  };
  const restoreBook = (bookId) => {
    setBusyDeleted(bookId);
    axios.post(`${API}/novels/${bookId}/restore`, { token })
      .then(refreshDeleted)
      .catch(err => setErrorMsg(`couldn't restore it: ${describeError(err)}`))
      .finally(() => setBusyDeleted(''));
  };
  const eraseBook = (bookId) => {
    setBusyDeleted(bookId);
    axios.delete(`${API}/novels/${bookId}/forever?token=${token}`)
      .then(() => { setConfirmForever(''); refreshDeleted(); })
      .catch(err => setErrorMsg(`couldn't delete it: ${describeError(err)}`))
      .finally(() => setBusyDeleted(''));
  };
  const restoreChapter = (c) => {
    setBusyDeleted(c.id);
    axios.post(`${API}/novels/${c.novel_id}/chapters/${c.id}/restore`, { token })
      .then(refreshDeleted)
      .catch(err => setErrorMsg(`couldn't restore it: ${describeError(err)}`))
      .finally(() => setBusyDeleted(''));
  };
  const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));

  const createNovel = () => {
    if (!newTitle.trim()) return;
    setErrorMsg('');
    if (!token) {
      setErrorMsg('no login token found — please log out and log back in');
      return;
    }
    setCreating(true);
    axios.post(`${API}/novels`, { token, title: newTitle.trim() })
      .then(res => {
        setShowModal(false);
        setNewTitle('');
        navigate(`/novels/${res.data.novel.id}`);
      })
      .catch(err => {
        setErrorMsg(`create failed: ${describeError(err)}`);
      })
      .finally(() => setCreating(false));
  };

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f',
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: 300,
      color: '#e8ddd0',
      display: 'flex', flexDirection: 'column',
      overflowY: 'hidden',
    },
    header: {
      padding: '20px 28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
      flexShrink: 0,
      zIndex: 50,
    },
    backBtn: {
      display: 'flex', alignItems: 'center', gap: '8px',
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: '#4a3d30', fontSize: '13px', fontFamily: "'DM Sans', sans-serif",
    },
    logo: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '20px', fontWeight: 300, fontStyle: 'italic',
      color: '#e8ddd0', letterSpacing: '0.04em',
    },
    plusBtn: {
      width: '34px', height: '34px', borderRadius: '50%', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)',
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0,
    },
    gearBtn: {
      width: '34px', height: '34px', borderRadius: '50%',
      border: '1px solid rgba(201,147,90,0.2)', background: 'transparent',
      cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0,
    },
    headerRight: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
    settingsModal: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '520px',
      maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    },
    settingsHint: {
      fontSize: '12px', color: '#4a3d30', lineHeight: 1.7, marginBottom: '14px',
    },
    promptTextarea: {
      width: '100%', minHeight: '240px', background: '#120f18',
      border: '1px solid rgba(201,147,90,0.15)', borderRadius: '12px',
      padding: '14px', color: '#e8ddd0', fontSize: '13px', lineHeight: 1.7,
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      boxSizing: 'border-box', outline: 'none', resize: 'vertical',
      marginBottom: '10px', flex: 1,
    },
    promptStatus: {
      fontSize: '11px', color: '#c9935a', marginBottom: '14px', minHeight: '14px',
    },
    // the whole width scrolls (so you can swipe on the sides on an iPad),
    // while the content itself stays the same 680px column in the middle
    body: {
      flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
      paddingTop: '32px', paddingBottom: '80px',
      paddingLeft: 'max(24px, calc((100% - 632px) / 2))', paddingRight: 'max(24px, calc((100% - 632px) / 2))',
      width: '100%', boxSizing: 'border-box',
    },
    pageTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '30px', fontWeight: 300, fontStyle: 'italic', color: '#e8ddd0',
      marginBottom: '4px',
    },
    pageSub: { fontSize: '13px', color: '#4a3d30', marginBottom: '16px' },
    errorBanner: {
      background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)',
      borderRadius: '12px', padding: '12px 16px', marginBottom: '16px',
      fontSize: '12px', color: '#e0a0a0', wordBreak: 'break-word',
    },
    novelCard: {
      background: '#1e1825',
      border: '1px solid rgba(201,147,90,0.06)',
      borderRadius: '18px', padding: '16px 18px',
      display: 'flex', alignItems: 'center', gap: '14px',
      cursor: 'pointer', marginBottom: '10px',
      WebkitUserSelect: 'none', userSelect: 'none',
    },
    novelIcon: {
      width: '52px', height: '52px', borderRadius: '12px',
      background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '20px', flexShrink: 0,
      boxShadow: '0 0 0 2px rgba(201,147,90,0.1)',
      overflow: 'hidden',
    },
    novelCoverImg: { width: '100%', height: '100%', objectFit: 'cover' },
    novelInfo: { flex: 1, overflow: 'hidden' },
    novelName: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '19px', fontWeight: 300, color: '#e8ddd0', marginBottom: '3px',
    },
    novelSub: {
      fontSize: '12px', color: '#4a3d30', fontWeight: 300,
      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    },
    statusChip: (status) => ({
      padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
      background: status === 'dropped' ? 'rgba(200,106,106,0.14)'
        : status === 'finished' ? 'rgba(127,199,154,0.12)'
        : 'rgba(201,147,90,0.14)',
      color: status === 'dropped' ? '#e0a0a0' : status === 'finished' ? '#9fd4b1' : '#c9935a',
    }),
    tabs: {
      display: 'flex', gap: '6px', overflowX: 'auto', marginBottom: '18px',
      paddingBottom: '2px', WebkitOverflowScrolling: 'touch',
    },
    tab: (active) => ({
      padding: '6px 14px', borderRadius: '16px', whiteSpace: 'nowrap', flexShrink: 0,
      border: active ? 'none' : '1px solid rgba(201,147,90,0.15)',
      background: active ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent',
      color: active ? '#0e0b0f' : '#8a7466', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: active ? 400 : 300,
    }),
    yearHeader: {
      fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase',
      color: '#8a7466', margin: '18px 0 10px',
    },
    deletedRow: { textAlign: 'center', marginTop: '28px' },
    spendingBtn: {
      display: 'inline-flex', alignItems: 'center', gap: '8px', margin: '0 0 26px', padding: '7px 14px 7px 8px',
      borderRadius: '18px', border: '1px solid rgba(201,147,90,0.22)', background: 'rgba(201,147,90,0.06)',
      color: '#e8ddd0', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    spendingCoin: {
      width: '22px', height: '22px', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '12px', fontWeight: 400,
    },
    deletedLink: {
      background: 'none', border: 'none', color: '#6d5d4e', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", textDecoration: 'underline', textUnderlineOffset: '3px',
    },
    deletedList: { display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', marginBottom: '16px', minHeight: '60px' },
    deletedItem: {
      display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '10px 12px', borderRadius: '12px',
      background: '#120f18', border: '1px solid rgba(201,147,90,0.08)',
    },
    deletedTitle: { fontSize: '14px', color: '#e8ddd0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    deletedSub: { fontSize: '11px', color: '#6d5d4e', marginTop: '2px' },
    deletedActions: { display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 },
    restoreBtn: {
      padding: '6px 14px', borderRadius: '16px', border: '1px solid rgba(201,147,90,0.3)', background: 'transparent',
      color: '#c9935a', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    },
    foreverBtn: {
      padding: '6px 14px', borderRadius: '16px', border: 'none', background: '#9c4545', color: '#f4e6e6',
      fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    },
    filterEmpty: { fontSize: '13px', color: '#4a3d30', padding: '20px 4px' },
    arrow: { color: '#4a3d30', fontSize: '16px', flexShrink: 0 },
    emptyState: {
      background: '#1e1825', border: '1px dashed rgba(201,147,90,0.15)',
      borderRadius: '18px', padding: '48px 24px',
      textAlign: 'center', marginBottom: '10px', cursor: 'pointer',
    },
    emptyText: { fontSize: '14px', color: '#4a3d30', lineHeight: 1.8 },
    modalOverlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200, padding: '24px',
    },
    modal: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '380px',
    },
    modalTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '22px', fontStyle: 'italic', color: '#e8ddd0', marginBottom: '18px',
    },
    modalInput: {
      width: '100%', background: '#120f18', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '10px', padding: '12px 14px', color: '#e8ddd0',
      fontSize: '14px', fontFamily: "'DM Sans', sans-serif", marginBottom: '18px',
      boxSizing: 'border-box', outline: 'none',
    },
    modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
    modalCancel: {
      padding: '9px 18px', borderRadius: '20px', border: 'none',
      background: 'transparent', color: '#4a3d30', fontSize: '13px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif",
    },
    modalCreate: {
      padding: '9px 18px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '13px', cursor: 'pointer', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif",
    },
  };

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      {/* HEADER */}
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => navigate('/companions')}>← back</button>
        <div style={s.logo}>Novels</div>
        <div style={s.headerRight}>
          <button style={s.gearBtn} onClick={openSettings} title="translation settings">
            <GearIcon size={15} color="#c9935a" />
          </button>
          <button style={s.plusBtn} onClick={() => setShowModal(true)} title="new novel">
            <PlusIcon size={16} color="#0e0b0f" />
          </button>
        </div>
      </header>

      {/* BODY */}
      <div style={s.body}>
        <div style={s.pageTitle}>Your Library</div>
        <div style={s.pageSub}>Write, translate, and export your chapters.</div>
        <button style={s.spendingBtn} onClick={() => setShowSpending(true)}>
          <span style={s.spendingCoin} aria-hidden="true">$</span> translation spending <span style={{ color: '#8a7466' }}>→</span>
        </button>

        {errorMsg && <div style={s.errorBanner}>⚠ {errorMsg}</div>}

        {loading ? null : novels.length === 0 ? (
          <div style={s.emptyState} onClick={() => setShowModal(true)}>
            <div style={s.emptyText}>no novels yet 📖<br />tap here to start your first one</div>
          </div>
        ) : (() => {
          const counts = {};
          novels.forEach(n => { if (n.reading_status) counts[n.reading_status] = (counts[n.reading_status] || 0) + 1; });
          const tabs = STATUS_TABS.filter(t => counts[t.value]);
          // a remembered filter that no longer matches anything falls back to all
          const active = filter !== 'all' && counts[filter] ? filter : 'all';
          const shown = active === 'all' ? novels : novels.filter(n => n.reading_status === active);

          const renderCard = (n) => (
            <div key={n.id} style={s.novelCard} onClick={() => navigate(`/novels/${n.id}`)}>
              <div style={s.novelIcon}>
                {n.cover_url
                  ? <img src={n.cover_url} alt={n.title} style={s.novelCoverImg} />
                  : '📖'
                }
              </div>
              <div style={s.novelInfo}>
                <div style={s.novelName}>{n.title}</div>
                <div style={s.novelSub}>
                  {n.reading_status && active === 'all' && (
                    <span style={s.statusChip(n.reading_status)}>{STATUS_LABELS[n.reading_status]}</span>
                  )}
                  {readingLine(n) && <span>{readingLine(n)}</span>}
                  <span>{n.chapter_count || 0} chapter{n.chapter_count === 1 ? '' : 's'}</span>
                </div>
              </div>
              <div style={s.arrow}>→</div>
            </div>
          );

          // finished and dropped books group by the year you finished or dropped them
          let body;
          if (active === 'finished' || active === 'dropped') {
            const byYear = {};
            shown.forEach(n => {
              const y = n.finished_month ? n.finished_month.slice(0, 4) : 'no date';
              (byYear[y] = byYear[y] || []).push(n);
            });
            const years = Object.keys(byYear).sort((a, b) =>
              a === 'no date' ? 1 : b === 'no date' ? -1 : b.localeCompare(a));
            body = years.map(y => (
              <div key={y}>
                <div style={s.yearHeader}>
                  {y} · {byYear[y].length} {active}
                </div>
                {byYear[y]
                  .slice()
                  .sort((a, b) => (b.finished_month || '').localeCompare(a.finished_month || ''))
                  .map(renderCard)}
              </div>
            ));
          } else {
            body = shown.map(renderCard);
          }

          return (
            <>
              {tabs.length > 0 && (
                <div style={s.tabs}>
                  <button style={s.tab(active === 'all')} onClick={() => pickFilter('all')}>
                    all {novels.length}
                  </button>
                  {tabs.map(t => (
                    <button key={t.value} style={s.tab(active === t.value)} onClick={() => pickFilter(t.value)}>
                      {t.label} {counts[t.value]}
                    </button>
                  ))}
                </div>
              )}
              {shown.length === 0
                ? <div style={s.filterEmpty}>nothing here yet</div>
                : body}
            </>
          );
        })()}

        {/* recently deleted stays down here */}
        {deletedCount > 0 && (
          <div style={s.deletedRow}>
            <button style={s.deletedLink} onClick={openDeleted}>recently deleted ({deletedCount})</button>
          </div>
        )}
      </div>

      {showSpending && <TranslationSpendingSheet onClose={() => setShowSpending(false)} />}

      {/* RECENTLY DELETED */}
      {showDeleted && (
        <div style={s.modalOverlay} onClick={() => setShowDeleted(false)}>
          <div style={s.settingsModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>recently deleted</div>
            <div style={s.settingsHint}>
              books stay here for {deleted?.days || 30} days, then they're gone for good. deleted chapters keep
              their text, translations and costs, and can always come back.
            </div>
            <div style={s.deletedList}>
              {!deleted && <div style={s.settingsHint}>loading…</div>}
              {deleted && deleted.books.length === 0 && deleted.chapters.length === 0 && (
                <div style={s.settingsHint}>nothing here.</div>
              )}
              {deleted?.books.map(b => (
                <div key={b.id} style={s.deletedItem}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.deletedTitle}>{b.title}</div>
                    <div style={s.deletedSub}>
                      book · {b.chapter_count} chapter{b.chapter_count === 1 ? '' : 's'} · gone in {daysLeft(b.gone_on)} day{daysLeft(b.gone_on) === 1 ? '' : 's'}
                    </div>
                  </div>
                  {confirmForever === b.id ? (
                    <span style={s.deletedActions}>
                      <button style={s.foreverBtn} disabled={busyDeleted === b.id} onClick={() => eraseBook(b.id)}>
                        {busyDeleted === b.id ? '…' : 'delete forever'}
                      </button>
                      <button style={s.modalCancel} onClick={() => setConfirmForever('')}>keep</button>
                    </span>
                  ) : (
                    <span style={s.deletedActions}>
                      <button style={s.restoreBtn} disabled={busyDeleted === b.id} onClick={() => restoreBook(b.id)}>
                        {busyDeleted === b.id ? '…' : 'restore'}
                      </button>
                      <button style={s.modalCancel} onClick={() => setConfirmForever(b.id)}>delete now</button>
                    </span>
                  )}
                </div>
              ))}
              {deleted?.chapters.map(c => (
                <div key={c.id} style={s.deletedItem}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.deletedTitle}>{c.title || 'untitled chapter'}</div>
                    <div style={s.deletedSub}>chapter · from {c.novel_title}</div>
                  </div>
                  <span style={s.deletedActions}>
                    <button style={s.restoreBtn} disabled={busyDeleted === c.id} onClick={() => restoreChapter(c)}>
                      {busyDeleted === c.id ? '…' : 'restore'}
                    </button>
                  </span>
                </div>
              ))}
            </div>
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={() => setShowDeleted(false)}>close</button>
            </div>
          </div>
        </div>
      )}

      {/* NEW NOVEL MODAL */}
      {showModal && (
        <div style={s.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>new novel</div>
            <input
              style={s.modalInput}
              placeholder="title"
              value={newTitle}
              autoFocus
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createNovel()}
            />
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={() => setShowModal(false)}>cancel</button>
              <button style={s.modalCreate} onClick={createNovel} disabled={creating}>
                {creating ? 'creating…' : 'create'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* TRANSLATION PROMPT SETTINGS MODAL */}
      {showSettings && (
        <div style={s.modalOverlay} onClick={() => setShowSettings(false)}>
          <div style={s.settingsModal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Translation Prompt</div>
            <div style={s.settingsHint}>
              These are the instructions sent to the model with every translation.
              Your chapter's Korean text is attached automatically — you don't need
              to reference it here.
            </div>
            {loadingPrompt ? (
              <div style={s.settingsHint}>loading…</div>
            ) : (
              <>
                <textarea
                  style={s.promptTextarea}
                  value={promptDraft}
                  onChange={e => { setPromptDraft(e.target.value); setPromptSaved(false); }}
                  placeholder="translation instructions…"
                />
                <div style={s.promptStatus}>
                  {promptSaved
                    ? 'saved ✓'
                    : isCustomPrompt
                      ? 'using your custom prompt'
                      : 'using the built-in default'}
                </div>
              </>
            )}
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={resetPrompt} disabled={savingPrompt}>
                reset to default
              </button>
              <button style={s.modalCancel} onClick={() => setShowSettings(false)}>close</button>
              <button style={s.modalCreate} onClick={savePrompt} disabled={savingPrompt}>
                {savingPrompt ? 'saving…' : 'save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
